<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';
import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
import CreateDeviceClassModal from '@/components/modals/CreateDeviceClassModal.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import Class from '@/api/class';
import { getClassIconUrl, withNormalizedClassIcon } from '@/tools/classIconUrl';

const store = useStore();
const user = computed(() => store.state.user);

const hasAccess = computed(() => {
  const u = user.value;
  return u && (
    u.role === 'admin' ||
    (u.permissions && (
      u.permissions.includes('createClass') ||
      u.permissions.includes('editClass') ||
      u.permissions.includes('deleteClass')
    ))
  );
});

const canCreate = computed(() => {
  const u = user.value;
  return u && (u.role === 'admin' || (u.permissions && u.permissions.includes('createClass')));
});

const canEdit = computed(() => {
  const u = user.value;
  return u && (u.role === 'admin' || (u.permissions && u.permissions.includes('editClass')));
});

const canDelete = computed(() => {
  const u = user.value;
  return u && (u.role === 'admin' || (u.permissions && u.permissions.includes('deleteClass')));
});

// Current entity for which we're managing classes (same as dashboard: entityViewing or fallback to user.entity)
const entityId = computed(() => {
  const ev = user.value?.entityViewing;
  if (ev && Array.isArray(ev) && ev.length > 0) return ev[0];
  return user.value?.entity || null;
});

const classes = ref([]);
const units = ref([]);
const loading = ref(true);
const modal = ref(null);
const editingClass = ref(null);
const submitting = ref(false);
const submitError = ref('');
const deleteConfirmClass = ref(null);
const deleteConfirmSubmitting = ref(false);
const deleteConfirmError = ref('');

const unitsLoading = ref(false);

function closeDeleteConfirm() {
  if (deleteConfirmSubmitting.value) return;
  deleteConfirmClass.value = null;
  deleteConfirmError.value = '';
}

// Load units available to this entity (same query as MainDashboard)
async function loadUnits() {
  const entity = user.value?.entityViewing || (entityId.value ? [entityId.value] : null);
  if (!entity || !Array.isArray(entity) || entity.length === 0) {
    units.value = [];
    return;
  }
  unitsLoading.value = true;
  try {
    const q = query(
      collection(db, 'data'),
      where('entities', 'array-contains-any', entity)
    );
    const snapshot = await getDocs(q);
    units.value = snapshot.docs.map((d) => ({
      dataID: d.id,
      friendlyName: d.data().friendlyName || d.id
    }));
  } catch (e) {
    console.error('Error loading units for device classes:', e);
    units.value = [];
  } finally {
    unitsLoading.value = false;
  }
}

// Load classes - replace this with your API when ready
async function loadClasses() {
  loading.value = true;
  const response = await Class.get();
  if (response.status === 200) {
    const classesData = await response.json();
    const raw = Array.isArray(classesData)
      ? classesData
      : (classesData?.classes ?? classesData?.details);
    classes.value = (raw || []).map(withNormalizedClassIcon);
  } else {
    console.error('Error loading device classes:', response.error);
    classes.value = [];
  }
  loading.value = false;
}

onMounted(async () => {
  await loadUnits();
  await loadClasses();
});

function openCreateModal() {
  submitError.value = '';
  editingClass.value = null;
  modal.value = 'create';
}

function openEditModal(cls) {
  submitError.value = '';
  editingClass.value = normalizeClassForForm(cls);
  modal.value = 'edit';
}

function closeModal() {
  modal.value = null;
  editingClass.value = null;
  submitError.value = '';
}

function normalizeClassForForm(cls) {
  if (!cls) return null;
  const unitIds = cls.unitIds ?? cls.devices ?? [];
  return { ...cls, unitIds: [...unitIds], iconUrl: getClassIconUrl(cls) };
}

function getUnitCount(classId) {
  const cls = classes.value.find((c) => c.id === classId);
  return (cls?.unitIds ?? cls?.devices ?? []).length;
}

async function handleCreate(payload) {
  const { name: payloadName, description: payloadDescription, unitIds = [], icon, quickMetrics = [] } = payload || {};
  const payloadForApi = {
    name: payloadName,
    description: payloadDescription || '',
    devices: [...unitIds],
    quickMetrics: quickMetrics,
  };
  if (icon) {
    payloadForApi.icon = icon;
  }
  const response = await Class.post(payloadForApi);
  if (response.status === 200) {
    await loadClasses();
    closeModal();
  } else {
    const msg = typeof response.error === 'string' ? response.error : 'Could not create class.';
    submitError.value = msg;
    console.error('Error creating class:', response.error);
  }
}

async function handleUpdate(payload) {
  const { classId, name: payloadName, description: payloadDescription, unitIds = [], icon, quickMetrics = [] } = payload || {};
  const body = {
    name: payloadName,
    description: payloadDescription || '',
    devices: [...unitIds],
    quickMetrics: quickMetrics,
  };
  if (icon) {
    body.icon = icon;
  }
  const response = await Class.put(body, classId);
  if (response.status === 200) {
    await loadClasses();
    closeModal();
  } else {
    const msg = typeof response.error === 'string' ? response.error : 'Could not save class.';
    submitError.value = msg;
    console.error('Error updating class:', response.error);
  }
}

async function handleSubmit(payload) {
  submitting.value = true;
  submitError.value = '';
  try {
    if (payload?.classId) {
      await handleUpdate(payload);
    } else {
      await handleCreate(payload);
    }
  } catch (e) {
    console.error('Device class save failed:', e);
    submitError.value = 'Request failed. Check your connection and try again.';
  } finally {
    submitting.value = false;
  }
}

function openDeleteConfirm(cls) {
  deleteConfirmError.value = '';
  deleteConfirmClass.value = cls;
}

async function confirmDeleteClass() {
  const cls = deleteConfirmClass.value;
  if (!cls) return;
  deleteConfirmSubmitting.value = true;
  deleteConfirmError.value = '';
  try {
    const response = await Class.delete(cls.id);
    if (response.status === 200) {
      classes.value = classes.value.filter((c) => c.id !== cls.id);
      deleteConfirmClass.value = null;
    } else {
      const msg = typeof response.error === 'string' ? response.error : 'Could not delete class.';
      deleteConfirmError.value = msg;
      console.error('Error deleting class:', response.error);
    }
  } catch (e) {
    console.error('Device class delete failed:', e);
    deleteConfirmError.value = 'Request failed. Check your connection and try again.';
  } finally {
    deleteConfirmSubmitting.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col h-full">
    <div v-if="!hasAccess" class="flex flex-col items-center justify-center h-full">
      <h1 class="text-3xl md:text-5xl my-4 md:my-6 font-bold text-mc-red">Access Denied</h1>
      <p class="text-xl text-ink-muted dark:text-on-dark-muted">You do not have permission to access this page.</p>
      <p class="text-lg text-ink-muted dark:text-on-dark-muted mt-2">Required permissions:
        <span class="font-mono bg-mc-steel/30 dark:bg-white/10 px-2 py-1 rounded-mc-sm text-mc-ink dark:text-on-dark">createClass</span>,
        <span class="font-mono bg-mc-steel/30 dark:bg-white/10 px-2 py-1 rounded-mc-sm text-mc-ink dark:text-on-dark">editClass</span>, or
        <span class="font-mono bg-mc-steel/30 dark:bg-white/10 px-2 py-1 rounded-mc-sm text-mc-ink dark:text-on-dark">deleteClass</span>
      </p>
    </div>

    <template v-else>
    <p class="mt-4 text-xs font-semibold uppercase tracking-wide text-ink-muted dark:text-on-dark-muted">Manage</p>
    <h1 class="text-2xl font-bold text-mc-ink dark:text-on-dark">Device Classes</h1>
    <p class="mt-1 mb-3 text-sm text-ink-muted dark:text-on-dark-muted">Configure class definitions and unit assignments for Manage workflows.</p>

    <div v-if="!entityId" class="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 text-amber-800 dark:text-amber-200">
      No entity selected. Select an entity from the header to manage device classes.
    </div>

    <template v-else>
      <div v-if="loading" class="flex flex-col items-center mt-10">
        <LoadingAnimation3 message="Loading device classes..." />
      </div>

      <div v-else>
        <button
          v-if="canCreate"
          type="button"
          @click="openCreateModal"
          class="btn-primary w-fit mt-2 mb-6"
        >
          + Add class
        </button>

        <div v-if="classes.length === 0" class="rounded-mc-md border border-mc-steel dark:border-line-on-dark bg-mc-cloud dark:bg-black/20 p-6 text-ink-muted dark:text-on-dark-muted">
          <span v-if="canCreate">No device classes yet. Click "Add class" to create one.</span>
          <span v-else>No device classes yet.</span>
        </div>

        <ul v-else class="space-y-4">
          <li
            v-for="cls in classes"
            :key="cls.id"
            class="ui-card flex flex-wrap items-center justify-between gap-3 p-4"
          >
            <div class="flex items-center gap-3 min-w-0 flex-1">
              <!-- Class icon -->
              <div class="w-12 h-12 rounded-mc-sm border border-mc-steel dark:border-line-on-dark bg-mc-cloud dark:bg-black/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img v-if="cls.iconUrl" :src="cls.iconUrl" class="w-full h-full object-contain p-1" alt="class icon" />
                <svg v-else class="w-6 h-6 text-mc-steel dark:text-line-on-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4-4a3 3 0 014.24 0L16 16m-2-2l1.586-1.586a3 3 0 014.243 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div class="min-w-0">
                <h3 class="font-semibold text-lg text-mc-ink dark:text-on-dark">{{ cls.name }}</h3>
                <p v-if="cls.description" class="text-sm text-ink-muted dark:text-on-dark-muted mt-0.5 truncate">
                  {{ cls.description }}
                </p>
                <p class="text-sm text-ink-muted dark:text-on-dark-muted mt-0.5">
                  {{ getUnitCount(cls.id) }} unit(s)
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              <button
                v-if="canEdit"
                type="button"
                :disabled="deleteConfirmClass !== null"
                @click="openEditModal(cls)"
                class="px-3 py-1.5 text-mesh-brand-blue border border-mesh-brand-blue rounded-lg hover:bg-mesh-brand-blue hover:text-white transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                Edit
              </button>
              <button
                v-if="canDelete"
                type="button"
                :disabled="deleteConfirmClass !== null"
                @click="openDeleteConfirm(cls)"
                class="px-3 py-1.5 text-red-600 border border-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                Delete
              </button>
            </div>
          </li>
        </ul>
      </div>
    </template>

    <!-- Create / Edit class modal (reused for both) -->
    <CreateDeviceClassModal
      v-if="modal === 'create' || (modal === 'edit' && editingClass)"
      :units="units"
      :units-loading="unitsLoading"
      :class-details="editingClass"
      :submitting="submitting"
      :submit-error="submitError"
      @close="closeModal"
      @submit="handleSubmit"
    />

    <!-- Delete class confirmation -->
    <ConfirmModal
      v-if="deleteConfirmClass"
      title="Delete class"
      confirm-label="Delete"
      variant="danger"
      :loading="deleteConfirmSubmitting"
      :error="deleteConfirmError"
      @confirm="confirmDeleteClass"
      @cancel="closeDeleteConfirm"
    >
      <p class="mt-3 text-ink-muted dark:text-on-dark-muted">
        Delete
        <span class="font-semibold text-mc-ink dark:text-on-dark">"{{ deleteConfirmClass.name }}"</span>?
        Units will be unassigned from this class. This cannot be undone.
      </p>
    </ConfirmModal>
    </template>
  </div>
</template>
